import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { PlusCircle as PlusCircleIcon } from 'react-feather'
import {
  Box,
  Button,
  TablePagination,
  Tooltip,
  useMediaQuery,
} from '@material-ui/core'

import { SectionCard } from 'components'
import { ControlsTable, SelectControlModal } from './components'

import theme from 'theme'
import constants from 'constants/index'

const ControlsSection = ({
  documentId,
  documentStatus,
  readOnly = false,
  newControlDisabled = false,
  newControlTooltip = '',
  controls = {},
}) => {
  const { ACTIVE_STATUS_ID } = constants.documents

  const {
    linkedDocuments,
    isLoading,
    refresh = () => {},
    pagination = {},
  } = controls

  const { page, perPage, totalCount, onPageChange, onRowsPerPageChange } =
    pagination

  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'), {
    defaultMatches: true,
  })

  const [openControlModal, setOpenControlModal] = useState(false)

  // Somente documento ativo pode ser vinculado como evidência, então vincular um controle antes disso
  // só levaria o usuário até o modal para receber a recusa do backend. Sem status informado não há o
  // que checar e a seção segue como antes.
  const isDocumentLinkable =
    !documentStatus || documentStatus === ACTIVE_STATUS_ID

  const mountNewControlTooltip = () => {
    // O motivo vindo de fora (uma operação em andamento, por exemplo) descreve algo temporário e
    // mais específico do que o status do documento, então vem primeiro.
    if (newControlTooltip) return newControlTooltip

    if (!isDocumentLinkable) {
      return 'Somente documentos ativos podem ser vinculados como evidência de um controle.'
    }

    return ''
  }

  const disabledNewControl = newControlDisabled || !isDocumentLinkable

  return (
    <SectionCard
      title="Controles"
      isTable
      hasPagination
      actions={
        !readOnly && (
          <Tooltip title={mountNewControlTooltip()}>
            <Box>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PlusCircleIcon />}
                onClick={() => setOpenControlModal(true)}
                disabled={disabledNewControl}
              >
                Novo controle
              </Button>
            </Box>
          </Tooltip>
        )
      }
    >
      <ControlsTable
        linkedDocuments={linkedDocuments}
        isLoading={isLoading}
        documentId={documentId}
        readOnly={readOnly}
        refresh={refresh}
      />
      <TablePagination
        component="div"
        count={totalCount || 0}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        page={(page || 1) - 1}
        rowsPerPage={perPage}
        rowsPerPageOptions={[5, 10, 25, 100]}
        labelRowsPerPage={isDesktop ? 'Por página' : ''}
        nextIconButtonProps={{ size: 'small' }}
        backIconButtonProps={{ size: 'small' }}
      />
      {!readOnly && openControlModal && (
        <SelectControlModal
          open={openControlModal}
          setOpen={setOpenControlModal}
          documentId={documentId}
          refresh={refresh}
        />
      )}
    </SectionCard>
  )
}

ControlsSection.propTypes = {
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  documentStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  readOnly: PropTypes.bool,
  newControlDisabled: PropTypes.bool,
  newControlTooltip: PropTypes.string,
  controls: PropTypes.shape({
    linkedDocuments: PropTypes.array,
    isLoading: PropTypes.bool,
    refresh: PropTypes.func,
    pagination: PropTypes.shape({
      page: PropTypes.number,
      perPage: PropTypes.number,
      totalCount: PropTypes.number,
      onPageChange: PropTypes.func,
      onRowsPerPageChange: PropTypes.func,
    }),
  }),
}

export default ControlsSection
